import React, { useState, useRef, useEffect } from "react";
import {
  Search,
  Send,
  Paperclip,
  Smile,
  MoreVertical,
  Phone,
  Video,
  Star,
  Bot,
  Sparkles,
  Clock,
  Check,
  CheckCheck,
  Filter,
  Plus,
  Zap,
  MessageCircle,
} from "lucide-react";

// Interfaces
interface Chat {
  id: number;
  name: string;
  phone: string;
  avatar: string;
  lastMessage: string;
  time: string;
  unread: number;
  status: "online" | "offline";
  country: string;
  tags: string[];
  assignedAgent: string;
}

interface Message {
  id: number;
  text: string;
  sender: "user" | "bot" | "agent";
  time: string;
  status: "sent" | "delivered" | "read";
  file?: {
    name: string;
    size: number;
    type: string;
  };
}

const WhatsAppInbox: React.FC = () => {
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
  const [messageInput, setMessageInput] = useState<string>("");
  const [showAISuggestions, setShowAISuggestions] = useState<boolean>(false);
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const [aiSuggestions, setAiSuggestions] = useState<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [showContactInfo, setShowContactInfo] = useState<boolean>(true);

  // Chat data
  const [chats] = useState<Chat[]>([
    {
      id: 1,
      name: "Anshul Jain",
      phone: "+91 9975596132",
      avatar: "A",
      lastMessage: "Hi! Thank you for your message. How can we help you today?",
      time: "07:32 PM",
      unread: 0,
      status: "online",
      country: "🇮🇳",
      tags: ["customer", "priority"],
      assignedAgent: "Bot",
    },
    {
      id: 2,
      name: "Sarah Wilson",
      phone: "+1 555-0123",
      avatar: "S",
      lastMessage: "I need help with my order",
      time: "06:45 PM",
      unread: 2,
      status: "offline",
      country: "🇺🇸",
      tags: ["support"],
      assignedAgent: "John Doe",
    },
    {
      id: 3,
      name: "Rajesh Kumar",
      phone: "+91 9876543210",
      avatar: "R",
      lastMessage: "When will my product be delivered?",
      time: "05:30 PM",
      unread: 1,
      status: "online",
      country: "🇮🇳",
      tags: ["delivery"],
      assignedAgent: "Bot",
    },
  ]);

  const [messages, setMessages] = useState<Record<number, Message[]>>({
    1: [
      {
        id: 1,
        text: "Hi! Thank you for your message.",
        sender: "bot",
        time: "07:30 PM",
        status: "delivered",
      },
      {
        id: 2,
        text: "How can we help you today?",
        sender: "bot",
        time: "07:30 PM",
        status: "delivered",
      },
      {
        id: 3,
        text: "I need information about your pricing plans",
        sender: "user",
        time: "07:31 PM",
        status: "read",
      },
    ],
    2: [
      {
        id: 1,
        text: "I need help with my order",
        sender: "user",
        time: "06:45 PM",
        status: "delivered",
      },
    ],
    3: [
      {
        id: 1,
        text: "When will my product be delivered?",
        sender: "user",
        time: "05:30 PM",
        status: "delivered",
      },
    ],
  });

  const generateAISuggestions = (_context: Chat | null): string[] => {
    const suggestions = [
      "I'd be happy to help you with our pricing plans. We offer three tiers: Basic, Professional, and Enterprise.",
      "Thank you for your inquiry! Let me provide you with detailed information about our pricing options.",
      "Our pricing is flexible and designed to meet different business needs. Would you like me to explain each plan?",
      "I can help you find the perfect plan for your requirements. What's your expected monthly usage?",
    ];
    return suggestions.slice(0, 3);
  };

  const handleChatSelect = (chat: Chat): void => {
    setSelectedChat(chat);
    setShowAISuggestions(false);
  };

  const handleSendMessage = (): void => {
    if ((!messageInput.trim() && attachedFiles.length === 0) || !selectedChat)
      return;

    const newMessage: Message = {
      id: Date.now(),
      text: messageInput,
      sender: "agent",
      time: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
      status: "sent",
      ...(attachedFiles.length > 0 && {
        file: {
          name: attachedFiles[0].name,
          size: attachedFiles[0].size,
          type: attachedFiles[0].type,
        },
      }),
    };

    setMessages((prev) => ({
      ...prev,
      [selectedChat.id]: [...(prev[selectedChat.id] || []), newMessage],
    }));

    setMessageInput("");
    setAttachedFiles([]);
    setShowAISuggestions(false);
  };

  const handleAISuggest = (): void => {
    const suggestions = generateAISuggestions(selectedChat);
    setAiSuggestions(suggestions);
    setShowAISuggestions(true);
  };

  const UseSuggestion = (suggestion: string): void => {
    setMessageInput(suggestion);
    setShowAISuggestions(false);
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const StatusIcon: React.FC<{ status: Message["status"] }> = ({ status }) => {
    switch (status) {
      case "sent":
        return <Check className="w-4 h-4 text-[#7B7B7B] font-semibold" />;
      case "delivered":
        return <CheckCheck className="w-4 h-4 text-[#7B7B7B] font-semibold" />;
      case "read":
        return <CheckCheck className="w-4 h-4 text-blue-500" />;
      default:
        return null;
    }
  };

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files) {
      setAttachedFiles(Array.from(e.target.files));
    }
  }

  function handleRemoveFile(index: number) {
    setAttachedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-green-500 rounded flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-gray-900">Team Inbox</span>
            </div>
            <div className="flex items-center space-x-2">
              <button className="p-2 hover:bg-gray-100 rounded">
                <Filter className="w-5 h-5 text-gray-500" />
              </button>
              <button className="p-2 hover:bg-gray-100 rounded">
                <Plus className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#7B7B7B] font-semibold" />
            <input
              type="text"
              placeholder="Search contacts..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Chat List */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-2">
            <div className="flex items-center justify-between px-2 py-1 text-sm text-gray-600">
              <span>Active Chats</span>
              <span className="bg-gray-200 px-2 py-1 rounded-full text-xs">
                {chats.length} Chats • 0 Unread
              </span>
            </div>
          </div>

          {chats.map((chat) => (
            <div
              key={chat.id}
              onClick={() => handleChatSelect(chat)}
              className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                selectedChat?.id === chat.id
                  ? "bg-green-50 border-l-4 border-l-green-500"
                  : ""
              }`}
            >
              <div className="flex items-start space-x-3">
                <div className="relative">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center text-white font-semibold">
                    {chat.avatar}
                  </div>
                  {chat.status === "online" && (
                    <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white"></div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <h3 className="font-semibold text-gray-900 truncate">
                        {chat.name}
                      </h3>
                      <span className="text-sm">{chat.country}</span>
                    </div>
                    <span className="text-xs text-gray-500">{chat.time}</span>
                  </div>

                  <div className="flex items-center justify-between mt-1">
                    <p className="text-sm text-gray-600 truncate">
                      {chat.lastMessage}
                    </p>
                    {chat.unread > 0 && (
                      <span className="bg-green-500 text-white text-xs rounded-full px-2 py-1 ml-2">
                        {chat.unread}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center justify-between mt-2">
                    <div className="flex space-x-1">
                      {chat.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <span className="text-xs text-gray-500">
                      Agent: {chat.assignedAgent}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {selectedChat ? (
          <>
            {/* Chat Header */}
            <div className="bg-white border-b border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="relative">
                    <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-semibold">
                      {selectedChat.avatar}
                    </div>
                    {selectedChat.status === "online" && (
                      <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white"></div>
                    )}
                  </div>
                  <div>
                    <h2 className="font-semibold text-gray-900">
                      {selectedChat.name}
                    </h2>
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                      <span>{selectedChat.status}</span>
                      <span>•</span>
                      <span>{selectedChat.phone}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <button className="p-2 hover:bg-gray-100 rounded">
                    <Phone className="w-5 h-5 text-gray-500" />
                  </button>
                  <button className="p-2 hover:bg-gray-100 rounded">
                    <Video className="w-5 h-5 text-gray-500" />
                  </button>
                  <button className="p-2 hover:bg-gray-100 rounded">
                    <Star className="w-5 h-5 text-gray-500" />
                  </button>
                  <button className="p-2 hover:bg-gray-100 rounded">
                    <MoreVertical className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
              {messages[selectedChat.id]?.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${
                    message.sender === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div className="flex items-end space-x-2 max-w-xs lg:max-w-md">
                    {message.sender !== "user" && (
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                        {message.sender === "bot" ? (
                          <Bot className="w-4 h-4" />
                        ) : (
                          "A"
                        )}
                      </div>
                    )}

                    <div>
                      <div
                        className={`px-4 py-2 rounded-lg ${
                          message.sender === "user"
                            ? "bg-green-500 text-white"
                            : message.sender === "bot"
                            ? "bg-blue-100 text-gray-800"
                            : "bg-white text-gray-800"
                        }`}
                      >
                        <p className="text-sm">{message.text}</p>
                      </div>

                      <div
                        className={`flex items-center space-x-1 mt-1 text-xs text-gray-500 ${
                          message.sender === "user"
                            ? "justify-end"
                            : "justify-start"
                        }`}
                      >
                        <span>{message.time}</span>
                        {message.sender !== "user" && (
                          <StatusIcon status={message.status} />
                        )}
                      </div>
                    </div>
                  </div>
                  {message.file && (
                    <div
                      className={`px-4 py-2 rounded-lg ${
                        message.sender === "user"
                          ? "bg-green-500 text-white"
                          : message.sender === "bot"
                          ? "bg-blue-100 text-gray-800"
                          : "bg-white text-gray-800"
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">
                        {message.text}
                      </p>

                      {message.file && (
                        <div className="mt-2 p-2 border border-gray-300 rounded bg-gray-100 text-sm">
                          <p className="font-medium text-gray-700">
                            {message.file.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {(message.file.size / 1024).toFixed(1)} KB •{" "}
                            {message.file.type}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}

              <div ref={messagesEndRef} />
            </div>

            {/* AI Suggestions */}
            {showAISuggestions && (
              <div className="bg-blue-50 border-t border-blue-200 p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <Sparkles className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold text-blue-800">
                    AI Suggested Responses
                  </span>
                </div>
                <div className="space-y-2">
                  {aiSuggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => UseSuggestion(suggestion)}
                      className="w-full text-left p-3 bg-white rounded-lg border border-blue-200 hover:border-blue-400 transition-colors text-sm"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Message Input */}
            <div className="bg-white border-t border-gray-200 p-4">
              <div className="flex items-end space-x-3">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <button
                      onClick={handleAISuggest}
                      className="flex items-center space-x-2 px-3 py-1 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full text-sm hover:from-blue-600 hover:to-purple-700 transition-all"
                    >
                      <Zap className="w-4 h-4" />
                      <span>AI Assist</span>
                    </button>

                    <label className="p-2 hover:bg-gray-100 rounded cursor-pointer">
                      <Paperclip className="w-5 h-5 text-gray-500" />
                      <input
                        type="file"
                        multiple
                        className="hidden"
                        onChange={handleFileChange}
                      />
                    </label>

                    {attachedFiles.length > 0 && (
                      <div className="mb-2 space-y-1">
                        {attachedFiles.map((file, idx) => (
                          <div
                            key={idx}
                            className="flex justify-between items-center px-3 py-1 text-sm bg-gray-100 rounded"
                          >
                            <div className="truncate max-w-[70%]">
                              <span>{file.name}</span>
                              <span className="ml-2 text-gray-500 text-xs">
                                {(file.size / 1024).toFixed(1)} KB
                              </span>
                            </div>

                            <button
                              onClick={() => handleRemoveFile(idx)}
                              className="text-red-500 hover:text-red-700 text-sm ml-2"
                            >
                              ❌
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex items-end space-x-2">
                    <div className="flex-1 border border-gray-300 rounded-lg">
                      <textarea
                        value={messageInput}
                        onChange={(e) => setMessageInput(e.target.value)}
                        placeholder="Type your message here..."
                        className="w-full p-3 border-none rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-green-500"
                        rows={2}
                        onKeyPress={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                      />
                    </div>

                    <button className="p-2 hover:bg-gray-100 rounded">
                      <Smile className="w-5 h-5 text-gray-500" />
                    </button>

                    <button
                      onClick={handleSendMessage}
                      className="bg-green-500 hover:bg-green-600 text-white p-3 rounded-lg transition-colors"
                    >
                      <Send className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>

              {isTyping && (
                <div className="flex items-center space-x-2 mt-2 text-sm text-gray-500">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div
                      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: "0.1s" }}
                    ></div>
                    <div
                      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: "0.2s" }}
                    ></div>
                  </div>
                  <span>AI is typing...</span>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Welcome to Team Inbox
              </h3>
              <p className="text-gray-600 mb-4">
                Select a chat to start messaging
              </p>
              <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
                <Bot className="w-5 h-5" />
                <span>AI Assistant ready to help</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Right Panel - Contact Info */}
      {selectedChat && showContactInfo && (
        <div className="w-80 bg-white border-l border-gray-200 p-4">
          <div className="flex justify-end mb-2">
            <button
              onClick={() => setShowContactInfo(false)}
              className="text-gray-500 hover:text-gray-800 text-sm"
            >
              Close Panel →
            </button>
          </div>
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center text-white text-2xl font-semibold mx-auto mb-3">
              {selectedChat.avatar}
            </div>
            <h3 className="font-semibold text-gray-900">{selectedChat.name}</h3>
            <p className="text-sm text-gray-500">{selectedChat.phone}</p>
            <div className="flex items-center justify-center space-x-1 mt-2">
              <span className="text-lg">{selectedChat.country}</span>
              <span className="text-sm text-gray-500">Available</span>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">
                Contact Parameters
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Language</span>
                  <span className="text-sm font-medium">en</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Name</span>
                  <span className="text-sm font-medium">
                    {selectedChat.name}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Tags</h4>
              <div className="flex flex-wrap gap-2">
                {selectedChat.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="bg-gray-200 text-gray-700 px-3 py-1 rounded-full text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Notes</h4>
              <p className="text-sm text-gray-600">
                Notes help you to keep track of your conversation with your team
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppInbox;
